import { FC, useState, useEffect } from "react";
import { EMPTY_ORDER_SEARCH, IOrderFilterState } from "../../api/orderFilter";
import Button from "../form/Button";
import Input from "../form/Input";
import RotateIcon from "../common/RotateIcon";
import { icons } from "../../icons/IconSet";
import useClickByKey from "../../hooks/useClickByKey";
import { DATE_PRESETS, presetForRange, presetRange, TDatePreset } from "./orderDatePresets";

type TSearchField = keyof typeof EMPTY_ORDER_SEARCH;

// -- Labels are the report's own column names, so the panel reads like the
// -- table it filters.
const SEARCH_FIELDS: { key: TSearchField, label: string, width: string }[] = [
  { key: 'orderId',  label: 'OrderID', width: 'w-[120px]' },
  { key: 'id',       label: '#ID',     width: 'w-[90px]' },
  { key: 'fromSite', label: 'Site',    width: 'w-[150px]' },
  { key: 'siName',   label: 'Name',    width: 'w-[150px]' },
  { key: 'siEmail',  label: 'Email',   width: 'w-[190px]' },
  { key: 'siCity',   label: 'City',    width: 'w-[130px]' },
  { key: 'siState',  label: 'State',   width: 'w-[80px]' },
  { key: 'siZip',    label: 'Zip',     width: 'w-[100px]' },
];

const searchFieldKeys = Object.keys(EMPTY_ORDER_SEARCH) as TSearchField[];
const countActive = (filter: IOrderFilterState) =>
  searchFieldKeys.filter((key) => !!filter[key]?.trim()).length;

// --
// -- The date range, always visible, with the per-column search folded away
// -- behind a button next to it.
// --
// -- Values are applied on Apply / Search / Enter rather than on every
// -- keystroke - a half-typed email would otherwise fire a query per
// -- character. Picking a preset applies at once, since there is nothing left
// -- to finish typing.
// --
const OrdersFilter: FC<{
  filter: IOrderFilterState,
  onChange: (filter: IOrderFilterState) => void,
  disabled?: boolean,
}> = ({
  filter,
  onChange,
  disabled = false,
}) => {
  const [draft, setDraft] = useState<IOrderFilterState>(() => filter);
  // -- Starts open only when a search is already in force, so a filter carried
  // -- back from an earlier visit is never hidden from the person it affects.
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(() => countActive(filter) > 0);
  const { btnRef, onKeyUp } = useClickByKey();

  // -- Keep the fields honest when the filter is changed from outside.
  useEffect(() => setDraft(() => filter), [filter]);

  const selectedPreset = presetForRange(draft);
  const activeCount = countActive(filter);

  const handlePreset = (value: string) => {
    const preset = value as TDatePreset;
    // -- 'custom' is only ever a readout of hand-picked dates, never a choice.
    if (preset === 'custom') return;

    const next = { ...draft, ...presetRange(preset) };
    setDraft(() => next);
    onChange(next);
  };

  const set = (key: keyof IOrderFilterState) => (value: string) =>
    setDraft((prevState) => ({ ...prevState, [key]: value }));

  const isDirty = (Object.keys(draft) as (keyof IOrderFilterState)[])
    .some((key) => draft[key] !== filter[key]);

  const hasSearch = searchFieldKeys.some((key) => !!draft[key]);

  return (
    <div className="flex flex-col gap-3 grow">

      {/* Period, and the way in to the search panel */}
      <div className="flex flex-wrap gap-3 items-end">
        <Input
          label="Period"
          name="datePreset"
          size="small"
          className="w-[130px]"
          value={selectedPreset}
          disabled={disabled}
          handle={(e) => handlePreset(e.target.value)}
          options={[
            ...DATE_PRESETS.map(({ value, title }) => ({ value, title })),
            // -- Shown only while the dates match no preset.
            ...(selectedPreset === 'custom' ? [{ value: 'custom', title: 'Custom' }] : []),
          ]}
        />

        <Input
          label="From"
          name="createdFrom"
          type="date"
          size="small"
          className="w-[150px]"
          value={draft.createdFrom}
          disabled={disabled}
          handle={(e) => set('createdFrom')(e.target.value)}
          onKeyUp={onKeyUp}
        />

        <Input
          label="Till"
          name="createdTill"
          type="date"
          size="small"
          className="w-[150px]"
          value={draft.createdTill}
          disabled={disabled}
          handle={(e) => set('createdTill')(e.target.value)}
          onKeyUp={onKeyUp}
        />

        <Button
          btnRef={btnRef}
          size="small"
          type="submit"
          isNotFullWidth
          disabled={disabled || !isDirty}
          onClick={() => onChange(draft)}
          className="mb-[2px] px-6"
        >
          Apply
        </Button>

        <Button
          size="small"
          type="button"
          variant="outline"
          isNotFullWidth
          disabled={disabled}
          onClick={() => setIsSearchOpen((prevState) => !prevState)}
          className="mb-[2px]"
        >
          <span className="flex items-center gap-2 whitespace-nowrap">
            {icons('search', { width: '14px' })}
            Search
            {/* A count, because a search that is folded away still narrows the
                list and would otherwise be invisible. */}
            {activeCount > 0 && (
              <span className="bg-primary text-white rounded-full px-[7px] text-xs leading-5">
                {activeCount}
              </span>
            )}
            <RotateIcon isShown={isSearchOpen}>
              {icons('chevron-down', { width: '14px' })}
            </RotateIcon>
          </span>
        </Button>
      </div>

      {/* Per-column search */}
      {isSearchOpen && (
        <div className="flex flex-wrap gap-3 items-end border-t-[1px] border-border_color pt-3">
          {SEARCH_FIELDS.map(({ key, label, width }) => (
            <Input
              key={key}
              label={label}
              name={key}
              type="text"
              size="small"
              className={width}
              value={draft[key]}
              disabled={disabled}
              handle={(e) => set(key)(e.target.value)}
              onKeyUp={onKeyUp}
            />
          ))}

          {/*
            No submit of its own. "Apply" in the row above commits the whole
            draft, dates and search together, and Enter does the same from any
            field - a second button here would have read "Search" next to a
            "Search" that opens the panel, which is two different actions under
            one word.
          */}
          <Button
            size="small"
            type="button"
            variant="outline"
            isNotFullWidth
            // -- Clears the search boxes only. Wiping the date range too would
            // -- be a surprise, and "All" is one click away in the Period menu.
            disabled={disabled || !hasSearch}
            onClick={() => onChange({ ...draft, ...EMPTY_ORDER_SEARCH })}
            className="mb-[2px]"
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrdersFilter;
